import { Link } from "react-router-dom"
import {ArrowRight, Headphones, Wrench, Monitor, Server, HardDrive, Users, Wifi, Settings, CheckCircle2, Shield} from 'lucide-react'
import SectionHeading from "@/components/SectionHeading"
import InlineEdit from "@/components/InlineEdit"

const services = [
  {
    icon: Headphones,
    key: "svc_support",
    title: "Support informatique",
    need: "Vos équipes rencontrent des problèmes techniques au quotidien et perdent du temps.",
    solution: "Un support réactif avec prise en charge rapide par ticket, téléphone ou à distance.",
    benefits: ["Temps de réponse < 2h", "Suivi via portail client", "Équipe dédiée"],
  },
  {
    icon: Wrench,
    key: "svc_maintenance",
    title: "Maintenance préventive",
    need: "Les pannes imprévues perturbent votre activité et génèrent des coûts importants.",
    solution: "Contrats de maintenance avec interventions régulières et surveillance proactive.",
    benefits: ["Réduction des pannes de 80%", "Mises à jour automatisées", "Rapports mensuels"],
  },
  {
    icon: Monitor,
    key: "svc_depannage",
    title: "Dépannage à distance",
    need: "Un problème urgent bloque un collaborateur et nécessite une intervention immédiate.",
    solution: "Connexion à distance sécurisée pour un diagnostic et une résolution en temps réel.",
    benefits: ["Intervention en quelques minutes", "Aucun déplacement nécessaire", "Sécurisé et tracé"],
  },
  {
    icon: Server,
    key: "svc_parc",
    title: "Gestion de parc informatique",
    need: "Vous perdez la visibilité sur vos équipements, licences et configurations.",
    solution: "Inventaire complet, suivi du cycle de vie et gestion centralisée de votre parc.",
    benefits: ["Inventaire à jour en temps réel", "Optimisation des coûts", "Planification des renouvellements"],
  },
  {
    icon: Wifi,
    key: "svc_reseau",
    title: "Réseau & Infrastructure",
    need: "Votre réseau est lent, instable ou mal sécurisé.",
    solution: "Audit, conception et déploiement d'infrastructures réseau performantes et fiables.",
    benefits: ["Architecture optimisée", "WiFi professionnel", "VPN et accès sécurisés"],
  },
  {
    icon: HardDrive,
    key: "svc_sauvegarde",
    title: "Sauvegarde & Sécurité",
    need: "Vos données sont exposées aux risques de perte, vol ou ransomware.",
    solution: "Solutions de sauvegarde automatisées et stratégies de sécurité multicouches.",
    benefits: ["Sauvegarde quotidienne automatique", "Plan de reprise d'activité", "Protection antivirus avancée"],
  },
  {
    icon: Settings,
    key: "svc_conseil",
    title: "Conseil & Optimisation IT",
    need: "Vous souhaitez moderniser votre SI mais manquez de vision stratégique.",
    solution: "Audit complet et recommandations sur mesure pour optimiser votre environnement IT.",
    benefits: ["Audit gratuit initial", "Feuille de route claire", "ROI mesurable"],
  },
  {
    icon: Users,
    key: "svc_assistance",
    title: "Assistance utilisateurs",
    need: "Vos collaborateurs ne maîtrisent pas tous les outils mis à leur disposition.",
    solution: "Formation, accompagnement et support de proximité pour une adoption optimale.",
    benefits: ["Formations personnalisées", "Documentation sur mesure", "Support de proximité"],
  },
]

export default function ServicesPage() {
  return (
    <div className="min-h-screen pt-24 lg:pt-32">
      {/* Header */}
      <section className="section-padding pb-16">
        <SectionHeading
          badge="Nos services"
          title="Des solutions IT complètes"
          highlight="pour votre entreprise"
          description="De la maintenance préventive au déploiement d'infrastructure, LJJ IT couvre l'ensemble de vos besoins informatiques avec professionnalisme et réactivité."
        />
      </section>

      {/* Services list */}
      <section className="section-padding pb-20">
        <div className="space-y-8">
          {services.map((service, i) => (
            <div
              key={service.key}
              className="card !p-0 overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className="grid lg:grid-cols-[1fr,1.5fr] gap-0">
                {/* Left */}
                <div className="p-6 lg:p-8 bg-slate-900/80 border-b lg:border-b-0 lg:border-r border-slate-800/50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                      <service.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <InlineEdit contentKey={`${service.key}_title`} defaultValue={service.title} as="h3" className="text-xl font-bold text-white" />
                  </div>
                  <div className="mb-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Votre besoin</span>
                    <InlineEdit contentKey={`${service.key}_need`} defaultValue={service.need} as="p" className="text-sm text-slate-400 leading-relaxed" multiline />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Notre solution</span>
                    <InlineEdit contentKey={`${service.key}_solution`} defaultValue={service.solution} as="p" className="text-sm text-slate-300 leading-relaxed" multiline />
                  </div>
                </div>

                {/* Right */}
                <div className="p-6 lg:p-8 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 block">Bénéfices</span>
                    <div className="space-y-2.5">
                      {service.benefits.map((b, j) => (
                        <div key={j} className="flex items-start gap-2.5">
                          <CheckCircle2 className="w-4.5 h-4.5 text-emerald-400 shrink-0 mt-0.5" />
                          <InlineEdit contentKey={`${service.key}_benefit_${j}`} defaultValue={b} as="span" className="text-sm text-slate-300" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6">
                    <Link to="/contact" className="btn-ghost !px-0 text-cyan-400 hover:text-cyan-300 text-sm">
                      Demander un devis
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-20">
        <div className="card !p-10 sm:!p-14 text-center bg-gradient-to-br from-cyan-500/5 to-emerald-500/5">
          <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <InlineEdit
            contentKey="services_cta_title"
            defaultValue="Besoin d'un accompagnement personnalisé ?"
            as="h2"
            className="text-2xl sm:text-3xl font-bold text-white mb-3"
          />
          <InlineEdit
            contentKey="services_cta_desc"
            defaultValue="Chaque entreprise est unique. Discutons de vos besoins pour construire la solution IT qui vous correspond."
            as="p"
            className="text-slate-400 max-w-lg mx-auto mb-6"
            multiline
          />
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/rendez-vous" className="btn-primary">
              Prendre rendez-vous
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
